import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import { Button, MarkdownContent, useToast } from '@/components/ui';
import { routes } from '@/lib/routes';
import {
  useAnswerAskUser,
  useCancelAskUser,
} from '@/hooks/useInbox';
import type {
  AskUserAnswer,
  AskUserRequest,
  InboxItem,
} from '@/types/inbox';
import { NoSpaceNotice } from './NoSpaceNotice';
import { RequestStatusBanner } from './RequestStatusBanner';

interface ReviewCardProps {
  item: InboxItem;
  request: AskUserRequest | undefined;
  isLoadingRequest?: boolean;
}

export function ReviewCard({
  item,
  request,
  isLoadingRequest,
}: ReviewCardProps) {
  const toast = useToast();
  const answer = useAnswerAskUser({ status: 'pending' });
  const cancel = useCancelAskUser({ status: 'pending' });

  const canAnswer = !!item.spaceId;
  const isSubmitting = answer.isPending || cancel.isPending;

  if (isLoadingRequest && !request) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading review…
      </div>
    );
  }

  // Status flipped from elsewhere — show the banner and keep the Open
  // conversation deep-link, but drop the approve/respond/dismiss actions.
  if (request && request.status !== 'pending') {
    const conversationHref = item.surfaceId
      ? `${routes.operator.chat(item.conversationId)}?focus=${item.surfaceId}`
      : routes.operator.chat(item.conversationId);
    return (
      <div className="space-y-3">
        <ReviewBody item={item} request={request} />
        <RequestStatusBanner item={item} request={request} />
        <Link to={conversationHref}>
          <Button variant="outline" size="sm">
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open conversation
          </Button>
        </Link>
      </div>
    );
  }

  // Reviews carry a single question (per backend handoff), but loop defensively
  // so multi-question reviews still work.
  const questions = request?.questions ?? [];

  function buildAnswers(value: string): AskUserAnswer[] {
    if (questions.length === 0) {
      return [{ questionId: 'review', value }];
    }
    return questions.map((q) => ({ questionId: q.questionId, value }));
  }

  async function submit(value: string, successMsg: string) {
    if (!item.spaceId) return;
    try {
      // Use item.refId (the askUserRequestId) rather than the fetched
      // request — the ?expand=source detail doesn't always inline, and the
      // action must still work. Mirrors the bulk-dismiss path.
      await answer.mutateAsync({
        spaceId: item.spaceId,
        requestId: item.refId,
        inboxItemId: item.inboxItemId,
        answers: buildAnswers(value),
      });
      toast.success(successMsg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not submit');
    }
  }

  async function approve() {
    await submit('approved', 'Approved');
  }

  async function dismiss() {
    if (!item.spaceId) return;
    try {
      await cancel.mutateAsync({
        spaceId: item.spaceId,
        requestId: item.refId,
        inboxItemId: item.inboxItemId,
        reason: 'user_dismissed',
      });
      toast.success('Dismissed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not dismiss');
    }
  }

  const conversationHref = item.surfaceId
    ? `${routes.operator.chat(item.conversationId)}?focus=${item.surfaceId}`
    : routes.operator.chat(item.conversationId);

  return (
    <div className="space-y-4">
      <ReviewBody item={item} request={request} />

      {!canAnswer && <NoSpaceNotice />}

      <div className="flex flex-wrap items-center gap-2">
        <Link to={conversationHref}>
          <Button variant="outline" size="sm">
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open conversation
          </Button>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            disabled={!canAnswer || isSubmitting}
          >
            Dismiss
          </Button>
          <Button
            size="sm"
            onClick={approve}
            disabled={!canAnswer || isSubmitting}
          >
            {answer.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
            )}
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
}

// Prefer the source question text — it preserves real newlines so markdown
// block elements (headings, lists, blockquotes) render. Falls back to the
// flattened `item.preview` when the source hasn't loaded yet.
function ReviewBody({
  item,
  request,
}: {
  item: InboxItem;
  request: AskUserRequest | undefined;
}) {
  const body = request?.questions?.[0]?.question || item.preview;
  if (!body) return null;
  return (
    <MarkdownContent size="sm" className="text-muted-foreground">
      {body}
    </MarkdownContent>
  );
}
